import { NgZone } from '@angular/core';

import { EditMode } from '../map/map-editing';
import { MapMode, MapTool, SuburbMapEngine } from '../map/suburb-map.engine';
import { SuburbCatalogueService } from './catalogue.service';
import { ContentRecordsService } from './content-records.service';
import { MapToolsService } from './map-tools.service';
import { PanelNavService } from './panel-nav.service';
import { SuburbPlacesService } from './suburb-places.service';
import { WorkspaceStateService } from './workspace-state.service';

export interface TestHandleDeps {
  zone: NgZone;
  state: WorkspaceStateService;
  catalogue: SuburbCatalogueService;
  places: SuburbPlacesService;
  records: ContentRecordsService;
  tools: MapToolsService;
  nav: PanelNavService;
}

/**
 * Development builds only: window.__suburbMap, the handle the Playwright specs use to read the map's state and drive
 * it where a canvas can't be clicked by selector (e2e/suburbs-256.spec.ts, e2e/subregions-260.spec.ts,
 * e2e/screenshots-257.spec.ts). `engine` is the map; `component` speaks the workspace's vocabulary the specs were
 * written against. This is its whole contract: a spec that needs more adds it here.
 */
export function suburbMapTestHandle(d: TestHandleDeps): {
  engine: SuburbMapEngine;
  component: object;
} {
  const { state, places } = d;
  const component = {
    zone: d.zone,
    get mode(): MapMode {
      return state.mode;
    },
    set mode(m: MapMode) {
      state.mode = m;
    },
    get tool(): MapTool {
      return state.tool;
    },
    set tool(t: MapTool) {
      state.tool = t;
    },
    get activeRegion(): string | null {
      return state.activeRegion;
    },
    set activeRegion(id: string | null) {
      state.activeRegion = id;
    },
    get activeSubRegion(): string | null {
      return state.activeSubRegion;
    },
    set activeSubRegion(id: string | null) {
      state.activeSubRegion = id;
    },
    get saving() {
      return state.saving;
    },
    get undoStack() {
      return state.undoStack;
    },
    get status() {
      return state.status;
    },
    get view() {
      return state.view;
    },
    get selectedRecord() {
      return d.records.suburbRecord(state.selectedCode);
    },
    get regions() {
      return d.catalogue.regions;
    },
    get subRegionRefs() {
      return d.catalogue.subRegionRefs;
    },
    subRegionColour: (id: string) => d.catalogue.subRegionColour(id),
    get places() {
      return places.places;
    },
    get kindOptions() {
      return places.kindOptions;
    },
    get placeKind(): string | null {
      return places.kind;
    },
    set placeKind(kind: string | null) {
      places.kind = kind;
    },
    drawPlaces: () => places.draw(),
    get popupPlace() {
      return places.popupPlace;
    },
    get pinPos() {
      return places.pin;
    },
    onStateChange: (code: string) => d.tools.changeState(code),
    pickResult: (code: string) => d.tools.pickResult(code),
    openSubRegion: (id: string) => d.nav.openSubRegion(id),
    quick: (mode: EditMode) => d.tools.quick(mode),
  };
  return { engine: state.engine, component };
}
